import { registerEvents } from "./skyward.ui.events";
import { registerLoadDOMCallback } from "./skyward.ui.window";
import { MenuItemDefinition, ModalMenu } from "./types";

const skyward = window.skyward;

const events =
{
    initToolbar
};

registerEvents("responsiveToolbar", events);

/**
 * The Scope object holds references to elements, measurements, and callback functions necessary for each responsive toolbar.
 */
type Scope =
    {
        toolbar: JQuery<HTMLElement>, // The toolbar container Div
        toolbarLeft: JQuery<HTMLElement>, // The left toolbar Div
        toolbarRight: JQuery<HTMLElement>, // The right toolbar Div
        moreButton: JQuery<HTMLElement>, // The button for the more menu
        moreMenuArea: JQuery<HTMLElement>, // The more menu container Div
        normalButtons?: JQuery<HTMLElement>,
        includeExternalButtons: boolean,
        cachedAvailableSpace?: number, // The available space in PX between the left and right toolbars
        cachedToolbarMargins?: number, // The combined left and right margins in PX for the toolbar container Div
        cachedToolbarWidth?: number, // The with of the toolbar container Div
        steps: StepResizeHandler[], // A series of callback functions that will be used to add/remove space for toolbar items
        menuIsEmpty?: boolean,
        alwaysHiddenButtons?: JQuery<HTMLElement>,
        rightToolbarItems?: JQuery<HTMLElement>,
        popupMenu?: ModalMenu
    };

/**
 * A callback function that will be called to show/hide elements within the toolbar based on how much space is available.
 * @typedef {function} StepResizeHandler
 * @this Scope
 * @property {boolean} makeSpace - True when elements should be hidden, False when elements should be made visible 
 * @property {JQuery<HTMLElement> | undefined} currentElement - If a selector was passed in when registering the handler function, the matching element will be passed in. 
 */
type StepResizeHandler = (this: Scope, makeSpace: boolean, currentElement?: JQuery<HTMLElement>) => boolean;

const WIGGLE_ROOM = 15; // A buffer to force items to disappear before they normally would. Helps correct flickering.

const classNames =
{
    leftToolbar: "toolbarLeft",
    rightToolbar: "toolbarRight",
    moreButton: "moreButton",
    moreMenuArea: "moreMenuArea",
    buttonText: "buttonText",
    moreMenuButton: "moreMenuButton",
    moreMenuButtonText: "js-moreButtonText",
    moreMenuAreaVisible: "moreMenuArea__moreButton--visible",
    moreMenuHide: "moreMenuHide",
    moreMenuAlwaysHide: "moreMenuAlwaysHide",
    uiHidden: "ui-hidden",
    uiDisabled: "ui-disabled",
    containsAllItems: "containsAllItems",
    toolbarHasWrappedContent: "toolbar--hasWrappedContent"
};

const dataNames =
{
    moreMenuClone: "moreMenuClone",
    responsiveToolbarOrigWidth: "responsiveToolbar--origWidth",
    includeExternalButtons: "include-external-buttons",
    toolbarRightWrappingWidth: "responsiveToolbar--toolbarRightWrappingWidth", // Cache the width of the right toolbar before the left toolbar starts wrapping
    toolbarNonWrappingHeight: "responsiveToolbar--toolbarNonWrappingHeight",   // Cache the height of the toolbar before the left toolbar starts wrapping
    toolbarHasCachedMarginTop: "responsiveToolbar--toolbarHasCachedMarginTop", // Cache whether the toolbar has cached the top offsets for the its items before the left toolbar starts wrapping
    toolbarWrappingMarginTop: "responsiveToolbar--toolbarWrappingMarginTop"    // Cache the top offset of elements within the left toolbar before the left toolbar starts wrapping
};

export let resize: () => void;

function initToolbar(context: JQuery<HTMLElement>)
{
    const scope: Scope =
    {
        toolbar: context,
        toolbarLeft: context.find(classNames.leftToolbar.dot()),
        toolbarRight: context.find(classNames.rightToolbar.dot()),
        moreButton: context.find(classNames.moreButton.dot()),
        moreMenuArea: context.find(classNames.moreMenuArea.dot()),
        includeExternalButtons: false,
        cachedAvailableSpace: undefined,
        cachedToolbarMargins: undefined,
        cachedToolbarWidth: undefined,
        steps: []
    };

    initMoreMenuArea(scope);

    generateSteps(scope);

    resize = getHandlerWithScopeClosure(resizeToolbar, scope);

    $(window).debounce("resize", resize, 10);

    registerLoadDOMCallback(resize);
}

function generateSteps(currentScope: Scope)
{
    addResponsiveStep(toggleVisibilityHandler, currentScope, currentScope.toolbarRight, ".js-hideFirst");
    addResponsiveStep(toggleVisibilityHandler, currentScope, currentScope.toolbarLeft, ".js-hideFirst");
    addResponsiveStep(toggleVisibilityHandler, currentScope, currentScope.toolbarLeft, ".buttonNameLabel");
    addResponsiveStep(moreMenuResizeHandler, currentScope, currentScope.moreButton);
    addResponsiveStep(toggleVisibilityHandler, currentScope, currentScope.toolbarRight, ".buttonText:not(.moreMenuArea .buttonText)");
    addResponsiveStep(toggleVisibilityHandler, currentScope, currentScope.toolbarRight, ".buttonSelectedValueLabel");
    addResponsiveStep(toggleVisibilityHandler, currentScope, currentScope.toolbarRight, ".buttonNameLabel");
    addResponsiveStep(toggleVisibilityHandler, currentScope, currentScope.toolbarLeft, ".buttonSelectedValueLabel");
    addResponsiveStep(toggleVisibilityHandler, currentScope, currentScope.toolbarLeft, ".selectorEELLabel,.buttonText");
    addResponsiveStep(toggleVisibilityHandler, currentScope, currentScope.moreButton, ".anchorText");
    addResponsiveStep(searchInputResizeHandler, currentScope, currentScope.toolbarLeft, ".selectorEELInput,.browseSearch");
    //this should always be the last step
    addResponsiveStep(controlsNewLineHandler, currentScope, currentScope.toolbarLeft);
}

function getHandlerWithScopeClosure(handler: ((scope: Scope) => void), scope: Scope)
{
    const currentScope = scope;
    const currentHandler = handler;
    return function ()
    {
        currentHandler(currentScope);
    };
}

function resizeToolbar(currentScope: Scope)
{
    if (!currentScope.toolbar.is(":visible"))
    {
        return false;
    }

    if (currentScope.cachedToolbarMargins === undefined)
    {
        currentScope.cachedToolbarMargins = parseFloat(currentScope.toolbarLeft.css("margin-left")) +
            parseFloat(currentScope.toolbarLeft.css("margin-right")) +
            parseFloat(currentScope.toolbarRight.css("margin-left")) +
            parseFloat(currentScope.toolbarRight.css("margin-right"));
    }

    currentScope.cachedToolbarWidth = currentScope.toolbar.width();

    if (getAvailableSpace(currentScope, true) < 0)
    {
        // Go forwards through the steps to make space
        for (let i = 0; i < currentScope.steps.length; i++)
        {
            if (currentScope.steps[i].apply(currentScope, [true]))
            {
                break;
            }
        }
    }
    else
    {
        // Go backwards through the steps to use up space
        for (let j = currentScope.steps.length - 1; j >= 0; j--)
        {
            if (currentScope.steps[j].apply(currentScope, [false]))
            {
                break;
            }
        }
    }

    return true;
}

/**
 * Add a handler function to Scope.steps that will be called on window resize to show/hide elements within the toolbar
 * @param {StepResizeHandler} handlerFunction - A function that will be called on Resize 
 * @param {Scope} currentScope - An instance of a Scope object that will be passed to the handlerFunction
 * @param {JQuery} container - The element within the toolbar that the StepResizeHandler will operate within.
 *                             If this element isn't found, the handlerFunction will not be added to Scope.steps
 * @param {string} [selector] - An optional selector to find specific elements within the container.
 *                              The handlerFunction will be called for each matching element.
 */
function addResponsiveStep(handlerFunction: StepResizeHandler, currentScope: Scope, container: JQuery<HTMLElement>, selector?: string)
{
    if (!container.length)
    {
        return;
    }
    if (!selector)
    {
        currentScope.steps.push(handlerFunction);
    }
    else
    {
        let elements = container.find(selector);
        // Reverse the order of elements in the right toolbar.  
        // They should be handled from right to left.
        if (container.is(currentScope.toolbarRight))
        {
            elements = $(elements.get().reverse());
        }

        elements.each(function ()
        {
            const jqElement = $(this);
            currentScope.steps.push(function (makeSpace)
            {
                return handlerFunction.apply(this, [makeSpace, jqElement]);
            });
        });
    }
}

function initMoreMenuArea(currentScope: Scope)
{
    if (!currentScope.moreMenuArea.length)
    {
        return;
    }

    currentScope.popupMenu = undefined;
    currentScope.menuIsEmpty = true;
    currentScope.rightToolbarItems = currentScope.moreMenuArea.siblings();
    currentScope.normalButtons = currentScope.moreButton.siblings().not(classNames.moreMenuAlwaysHide.dot());

    updateMoreMenuAlwaysHideButtons(currentScope);

    if (currentScope.moreMenuArea.data(dataNames.includeExternalButtons))
    {
        currentScope.includeExternalButtons = true;

        //flag moreMenuButtonText elements in the left toolbar so we can efficiently find them on resize to move them to the moreMenu
        currentScope.toolbarLeft.find(classNames.moreMenuButton.dot() + " " + classNames.buttonText.dot()).addClass(classNames.moreMenuButtonText);
    }

    currentScope.moreButton.on("click", getHandlerWithScopeClosure(moreButtonClicked, currentScope));

    setMoreButtonState(currentScope);
}

function updateMoreMenuAlwaysHideButtons(currentScope: Scope)
{
    currentScope.alwaysHiddenButtons = currentScope.moreMenuArea.find(classNames.moreMenuAlwaysHide.dot());
}

/**
 * A responsive Steps handler function.
 * Make space in the toolbar by moving more menu buttons inside the more menu in the right toolbar
 * @param {boolean} makeSpace - True when the toolbar is shrinking and elements need to be hidden.
 *                              False when the toolbar is expanding and elements can be shown.     
 * @returns {boolean} Returns True when the function successfully made/used the available space
 */
function moreMenuResizeHandler(this: Scope, makeSpace: boolean)
{
    const currentScope = this;

    if (currentScope.popupMenu)
    {
        currentScope.popupMenu.close();
        currentScope.popupMenu = undefined;
    }

    let madeChanges = false;

    if (currentScope.normalButtons === undefined)
    {
        return false;
    }

    if (makeSpace)
    {
        $(currentScope.normalButtons.not(classNames.moreMenuHide.dot()).get().reverse()).each(function (this: HTMLElement)
        {
            toggleMoreMenuButton(currentScope, $(this), false);
            madeChanges = true;
            if (getAvailableSpace(currentScope, true) > 0)
            {
                return false;
            }
            return;
        });
    }
    else
    {
        currentScope.normalButtons.filter(classNames.moreMenuHide.dot()).each(function (this: HTMLElement)
        {
            const button = $(this);
            let cachedWidth = button.data("cachedWidth");
            if (typeof cachedWidth === "undefined")
            {
                cachedWidth = button.outerWidth(true);
                button.data("cachedWidth", cachedWidth);
            }
            if (getAvailableSpace(currentScope) > cachedWidth)
            {
                toggleMoreMenuButton(currentScope, button, true);
                madeChanges = true;
                currentScope.cachedAvailableSpace = undefined;
                return;
            }

            return false;
        });
    }

    if (madeChanges)
    {
        return getAvailableSpace(currentScope) > 0;
    }

    return false;
}

function toggleMoreMenuButton(currentScope: Scope, button: JQuery<HTMLElement>, show: boolean)
{
    button.toggleClass(classNames.moreMenuHide, !show);

    setMoreButtonState(currentScope);
}

function setMoreButtonState(currentScope: Scope)
{
    const hasHiddenButtons = (currentScope.alwaysHiddenButtons && currentScope.alwaysHiddenButtons.length > 0) || (currentScope.normalButtons && currentScope.normalButtons.filter(classNames.moreMenuHide.dot()).length > 0);
    const hasVisibleButtons = currentScope.normalButtons && currentScope.normalButtons.not(classNames.moreMenuHide.dot()).length > 0;

    currentScope.moreButton.toggleClass(classNames.uiDisabled, !hasHiddenButtons).toggleClass(classNames.moreMenuAreaVisible, hasHiddenButtons);
    currentScope.moreButton.toggleClass(classNames.containsAllItems, !hasVisibleButtons);
    currentScope.menuIsEmpty = !hasHiddenButtons;
}

function moreButtonClicked(currentScope: Scope)
{
    if (currentScope.menuIsEmpty)
    {
        return;
    }
    const menuItems: MenuItemDefinition[] = [{ type: "title", text: "List Options" }];
    currentScope.normalButtons?.filter(classNames.moreMenuHide.dot()).add(currentScope.alwaysHiddenButtons!).not(classNames.uiHidden.dot()).each(function (this: HTMLElement)
    {
        const button = $(this);
        const words: string[] = [];
        button.find("span").each(function (_i, val)
        {
            words.push(val.innerText);
        });
        menuItems.push(
            {
                type: "item",
                text: words.join(" "),
                click: function ()
                {
                    button.trigger("click");
                },
                icon: button.find("svg"),
                disabled: button.hasClass(classNames.uiDisabled),
                attributes:
                {
                    role: button.attr("role"),
                    "aria-haspopup": button.attr("aria-haspopup")
                }
            });
    });
    currentScope.popupMenu = skyward.modalLayer.showMenu(menuItems, currentScope.moreButton, { beforeClose: function () { currentScope.moreButton.trigger("focus"); } });
}

/**
 * A responsive Steps handler function.
 * Make space in the toolbar by hiding elements 
 * @param {boolean} makeSpace - True when the toolbar is shrinking and elements need to be hidden.
 *                              False when the toolbar is expanding and elements can be shown.
 * @param {JQuery} element - The current element to apply width/visibility changes to
 * @returns {boolean} Returns True when the function successfully made/used the available space
 */
function toggleVisibilityHandler(this: Scope, makeSpace: boolean, element?: JQuery<HTMLElement>)
{
    const currentScope = this;

    if (element === undefined || !element.length || !document.body.contains(element[0]) || element.is(".js-dontHide"))
    {
        return false;
    }

    if (makeSpace)
    {
        hideToolbarButton(currentScope, element);
        if (getAvailableSpace(currentScope, true) >= 0)
        {
            return true;
        }
    }
    else
    {
        const cachedToolbarWidth = currentScope.cachedAvailableSpace;

        showToolbarButton(currentScope, element);

        if (getAvailableSpace(currentScope, true) < 0)
        {
            hideToolbarButton(currentScope, element);

            currentScope.cachedAvailableSpace = cachedToolbarWidth;

            if (getAvailableSpace(currentScope) < 0)
            {
                return true;
            }
        }
    }
    return false;
}

function hideToolbarButton(currentScope: Scope, element: JQuery<HTMLElement>)
{
    if (!currentScope.includeExternalButtons || !element.hasClass(classNames.moreMenuButtonText))
    {
        element.hide();
        return;
    }

    const button = element.closest(classNames.moreMenuButton.dot());
    let moreMenuClone = button.data(dataNames.moreMenuClone);
    if (!moreMenuClone)
    {
        moreMenuClone = button.clone().addClass(classNames.moreMenuAlwaysHide);
        button.data(dataNames.moreMenuClone, moreMenuClone);
    }
    button.hide();
    moreMenuClone.prependTo(currentScope.moreMenuArea);
    updateMoreMenuAlwaysHideButtons(currentScope);
    setMoreButtonState(currentScope);
}

function showToolbarButton(currentScope: Scope, element: JQuery<HTMLElement>)
{
    if (!currentScope.includeExternalButtons || !element.hasClass(classNames.moreMenuButtonText))
    {
        element.show();
        return;
    }

    const button = element.closest(classNames.moreMenuButton.dot());
    const moreMenuClone = button.data(dataNames.moreMenuClone);
    button.show();
    if (moreMenuClone)
    {
        moreMenuClone.detach();
    }
    updateMoreMenuAlwaysHideButtons(currentScope);
    setMoreButtonState(currentScope);
}

/**
 * A responsive Steps handler function.
 * Make space in the toolbar by shrinking the search input 
 * @param {boolean} makeSpace - True when the toolbar is shrinking and elements need to be hidden.
 *                              False when the toolbar is expanding and elements can be shown.
 * @param {JQuery} element - The current element to apply width/visibility changes to
 * @returns {boolean} Returns True when the function successfully made/used the available space
 */
function searchInputResizeHandler(this: Scope, makeSpace: boolean, element?: JQuery<HTMLElement>)
{
    if (element === undefined)
    {
        return false;
    }
    const currentScope = this;
    const minimumInputWidth = 50; //50px -- arbitrary minimum size
    let origWidth = element.data(dataNames.responsiveToolbarOrigWidth);
    const currentWidth = element[0].offsetWidth;

    let newWidth;

    if (!origWidth)
    {
        origWidth = currentWidth;
        element.data(dataNames.responsiveToolbarOrigWidth, origWidth);
    }
    const absSpaceAvailable = Math.abs(getAvailableSpace(currentScope));

    if (makeSpace)
    {
        newWidth = Math.max(currentWidth - absSpaceAvailable, minimumInputWidth);
        element.css("width", newWidth + "px");
        return element[0].offsetWidth < currentWidth;
    }
    else if (currentWidth < origWidth)
    {
        newWidth = Math.min(currentWidth + absSpaceAvailable, origWidth);
        element.css("width", newWidth + "px");
        return element[0].offsetWidth > currentWidth;
    }
    return false;
}

/**
 * A responsive Steps handler function.
 * Make space in the toolbar by wrapping the items in the left toolbar by enabling flex-wrap.
 * This needs to be the last responsive step handler; it sets a fixed-width on the left toolbar.
 * @param {boolean} makeSpace - True when the toolbar is shrinking and elements need to be hidden.
 *                              False when the toolbar is expanding and elements can be shown.
 *  @returns {boolean} Returns True when content in the left toolbar is wrapping
 */
function controlsNewLineHandler(this: Scope, makeSpace: boolean)
{
    const currentScope = this;

    // if there is space available and the toolbar isn't wrapping, we don't need to do anything
    if (!makeSpace && !currentScope.toolbar.hasClass(classNames.toolbarHasWrappedContent))
    {
        return false;
    }

    currentScope.toolbar.removeClass(classNames.toolbarHasWrappedContent);

    let toolbarRightWrappingWidth = currentScope.toolbarRight.data(dataNames.toolbarRightWrappingWidth);
    if (typeof toolbarRightWrappingWidth === "undefined")
    {
        toolbarRightWrappingWidth = currentScope.toolbarRight.outerWidth();
        currentScope.toolbarRight.data(dataNames.toolbarRightWrappingWidth, toolbarRightWrappingWidth);
    }

    let toolbarNonWrappingHeight = currentScope.toolbar.data(dataNames.toolbarNonWrappingHeight);
    if (typeof toolbarNonWrappingHeight === "undefined")
    {
        toolbarNonWrappingHeight = currentScope.toolbar.height();
        currentScope.toolbar.data(dataNames.toolbarNonWrappingHeight, toolbarNonWrappingHeight);
    }

    const toolbarHasCachedMarginTop = currentScope.toolbar.data(dataNames.toolbarHasCachedMarginTop);
    if (typeof toolbarHasCachedMarginTop === "undefined")
    {
        // get the top position of child elements when the toolbar is not wrapping.
        // This value will be used as a marginTop on each element to prevent shifting as items wrap
        currentScope.toolbarLeft.children().each(function ()
        {
            const childItem = $(this);
            childItem.data(dataNames.toolbarWrappingMarginTop, childItem.position().top);
        });
        currentScope.toolbar.data(dataNames.toolbarHasCachedMarginTop, true);
    }

    // clear overridden values        
    currentScope.toolbarRight.css("height", "");
    currentScope.toolbarLeft.children().css({ "marginTop": "", "height": "" });

    // set the width of the left toolbar to take up all available space
    currentScope.toolbarLeft.width(currentScope.cachedToolbarWidth! - toolbarRightWrappingWidth - WIGGLE_ROOM);

    currentScope.toolbar.addClass(classNames.toolbarHasWrappedContent);

    // if the height of the toolbar is has increased by adding the toolbarHasWrappedContent class, content is wrapping
    const toolbarLeftHeight = currentScope.toolbarLeft.height();
    const isContentWrapping = toolbarLeftHeight !== undefined && toolbarLeftHeight > toolbarNonWrappingHeight;

    if (isContentWrapping)
    {
        //set the height on the right toolbar when the left toolbar is wrapping to prevent items from shifting
        currentScope.toolbarRight.css("height", toolbarNonWrappingHeight);
        //set the marginTop on the left toolbar items when the left toolbar is wrapping to prevent items from shifting
        currentScope.toolbarLeft.children().each(function ()
        {
            const childItem = $(this);
            childItem.css(
                {
                    "marginTop": childItem.data(dataNames.toolbarWrappingMarginTop), //set marginTop to prevent items from shifting as items wrap
                    "height": toolbarNonWrappingHeight // set height to prevent items from crowding as they wrap
                });
        });
        currentScope.toolbarLeft.children().css("height", toolbarNonWrappingHeight);
    }
    else
    {
        //content didn't increase by adding the toolbarHasWrappedContent class so remove it and clear the toolbarLeft width override
        currentScope.toolbar.removeClass(classNames.toolbarHasWrappedContent);
        currentScope.toolbarLeft.css("width", "");
    }

    return isContentWrapping;
}

function getAvailableSpace(currentScope: Scope, recalculateSpace?: boolean)
{
    if (currentScope.cachedAvailableSpace === undefined || recalculateSpace === true)
    {
        const toolbarLeftWidth = currentScope.toolbarLeft.length > 0 ? currentScope.toolbarLeft[0].offsetWidth : 0;
        const toolbarRightWrappingWidth = currentScope.toolbarRight.length > 0 ? currentScope.toolbarRight[0].offsetWidth : 0;
        currentScope.cachedAvailableSpace = currentScope.cachedToolbarWidth! - toolbarLeftWidth - toolbarRightWrappingWidth - currentScope.cachedToolbarMargins! - WIGGLE_ROOM;
    }
    return currentScope.cachedAvailableSpace;
}